import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Flex,
  Spacer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import ReactMarkdown from 'react-markdown';
import VarianceDocumentViewer from './variance/VarianceDocumentViewer';
import { callApi } from '../callApi';

const MarkdownComponents = {
  h1: (props) => <Heading as="h1" size="xl" my={4} {...props} />,
  h2: (props) => <Heading as="h2" size="lg" my={3} {...props} />,
  h3: (props) => <Heading as="h3" size="md" my={2} {...props} />,
  p: (props) => <Text mb={2} {...props} />,
  ul: (props) => <Box as="ul" pl={4} mb={2} {...props} />,
  ol: (props) => <Box as="ol" pl={4} mb={2} {...props} />,
  li: (props) => <Box as="li" mb={1} {...props} />,
  strong: (props) => <Text as="strong" fontWeight="bold" {...props} />,
};

const cleanMarkdownText = (text) => {
  if (!text) return '';
  let cleaned = text.replace(/```markdown\n?/g, '');
  cleaned = cleaned.replace(/```\n?/g, '');
  return cleaned.trim();
};

const ClaimAnalysisDashboard = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [varianceData, setVarianceData] = useState(null);
  const [draftContent, setDraftContent] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);

  const fetchExistingData = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      // Fetch Variance Data
      const varianceResponse = await fetch(`${window.env.REACT_APP_REVEDY_API_HOST}/api/coding-variance/case/${caseId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (varianceResponse.ok) {
        const data = await varianceResponse.json();
        if (data.status === 'success' && data.report) {
          if (data.report.payer_denial_evaluation) {
            setVarianceData(data.report.payer_denial_evaluation);
          }
          if (data.report.eob_data && data.report.eob_data.draft_content) {
            setDraftContent(data.report.eob_data.draft_content);
          }
        } else {
          setVarianceData(null);
          setDraftContent(null);
        }
      }

      // Fetch Case Files for Document Viewer
      const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        setCaseFiles(detailsData.files || []);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      // Don't toast on 404s as it just means it hasn't been run yet
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (caseId && accessToken) {
      fetchExistingData();
    }
  }, [caseId, accessToken]);

  const handleRunAnalysis = async () => {
    if (!accessToken) return;
    setIsRunning(true);
    try {
      const response = await fetch(`${window.env.REACT_APP_REVEDY_API_HOST}/nma/analyze_claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          external_case_id: caseId,
          options: {
            force_reextraction: true // Force fresh run for the demo
          }
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast({
          title: 'Analysis Complete',
          description: 'Claim and EOB data successfully analyzed.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // The response contains the variance in results.variance.summary
        if (data.results && data.results.variance && data.results.variance.summary) {
          setVarianceData(data.results.variance.summary);
        } else {
          fetchExistingData(); // Fallback to fetching
        }
        
        if (data.results && data.results.strategy && data.results.strategy.draft_content) {
          setDraftContent(data.results.strategy.draft_content);
        }
      } else {
        throw new Error(data.message || 'Unknown error occurred');
      }
    } catch (error) {
      toast({
        title: 'Analysis Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRunning(false);
    }
  };

  const getPathwayColor = (pathway) => {
    if (!pathway) return 'gray';
    const p = pathway.toLowerCase();
    if (p.includes('correction')) return 'orange';
    if (p.includes('appeal')) return 'red';
    if (p.includes('arbitration')) return 'purple';
    return 'blue';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1200px" margin="0 auto">
        <Flex mb={6} align="center">
        <VStack align="start" spacing={1}>
          <Heading size="lg">Claim Analysis Dashboard</Heading>
          <Text color="gray.500">Case ID: {caseId}</Text>
        </VStack>
        <Spacer />
        <HStack spacing={4}>
          <Button 
            colorScheme="blue" 
            onClick={handleRunAnalysis} 
            isLoading={isRunning}
            loadingText="Analyzing..."
          >
            Run Analysis
          </Button>
        </HStack>
      </Flex>

      {isLoading ? (
        <Flex justify="center" p={12}>
          <Spinner size="xl" />
        </Flex>
      ) : (
        <VStack spacing={6} align="stretch">
          
          {/* Summary Cards - Only show if we have variance data */}
          {varianceData && (
            <HStack spacing={6} align="stretch">
              <Card flex={1}>
                <CardBody>
                  <VStack align="start">
                    <Text color="gray.500" fontSize="sm" fontWeight="bold" textTransform="uppercase">Total Billed</Text>
                    <Heading size="md">{formatCurrency(varianceData.billed_total)}</Heading>
                  </VStack>
                </CardBody>
              </Card>
              <Card flex={1}>
                <CardBody>
                  <VStack align="start">
                    <Text color="gray.500" fontSize="sm" fontWeight="bold" textTransform="uppercase">Total Paid</Text>
                    <Heading size="md">{formatCurrency(varianceData.paid_total)}</Heading>
                  </VStack>
                </CardBody>
              </Card>
              <Card flex={1}>
                <CardBody>
                  <VStack align="start">
                    <Text color="gray.500" fontSize="sm" fontWeight="bold" textTransform="uppercase">Recommended Action</Text>
                    <Badge 
                      colorScheme={getPathwayColor(varianceData.primary_pathway)} 
                      fontSize="md" 
                      p={2} 
                      borderRadius="md"
                    >
                      {varianceData.primary_pathway ? varianceData.primary_pathway.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                    </Badge>
                  </VStack>
                </CardBody>
              </Card>
            </HStack>
          )}

          {/* Tabbed Content Area - ALWAYS RENDERED */}
          <Tabs variant="enclosed" colorScheme="blue" mt={4}>
            <TabList>
              <Tab fontWeight="bold">Source Documents</Tab>
              <Tab fontWeight="bold">Variance Details</Tab>
              <Tab fontWeight="bold">Strategy Overview</Tab>
              <Tab fontWeight="bold">Corrected Claim Memo</Tab>
              <Tab fontWeight="bold">Formal Appeal Letter</Tab>
            </TabList>

            <TabPanels>
              {/* Tab 1: Source Documents (Always works if files exist) */}
              <TabPanel px={0}>
                <VarianceDocumentViewer 
                  caseId={caseId}
                  caseFiles={caseFiles} 
                  accessToken={accessToken} 
                />
              </TabPanel>

              {/* Tab 2: Line Items Table */}
              <TabPanel px={0}>
                {!varianceData ? (
                  <Card>
                    <CardBody>
                      <Text textAlign="center" color="gray.500" py={8}>
                        No variance analysis found. Click "Run Analysis" to process the documents.
                      </Text>
                    </CardBody>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <Heading size="md">Line Item Variance</Heading>
                    </CardHeader>
                    <Divider />
                    <CardBody>
                      <Box overflowX="auto">
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th>CPT Code</Th>
                              <Th isNumeric>Billed Amount</Th>
                              <Th isNumeric>Paid Amount</Th>
                              <Th>CARC</Th>
                              <Th>RARC</Th>
                              <Th>Pathway</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {varianceData.line_item_variance && varianceData.line_item_variance.length > 0 ? (
                              varianceData.line_item_variance.map((item, idx) => (
                                <Tr key={idx}>
                                  <Td fontWeight="bold">{item.cpt_code || 'N/A'}</Td>
                                  <Td isNumeric>{formatCurrency(item.billed_amount)}</Td>
                                  <Td isNumeric>{formatCurrency(item.paid_amount)}</Td>
                                  <Td>
                                    {item.carc ? <Badge colorScheme="red">{item.carc}</Badge> : '-'}
                                  </Td>
                                  <Td>
                                    {item.rarc ? <Badge colorScheme="orange">{item.rarc}</Badge> : '-'}
                                  </Td>
                                  <Td>
                                    <Badge colorScheme={getPathwayColor(item.dispute_pathway)}>
                                      {item.dispute_pathway ? item.dispute_pathway.replace('_', ' ') : 'standard appeal'}
                                    </Badge>
                                  </Td>
                                </Tr>
                              ))
                            ) : (
                              <Tr>
                                <Td colSpan={6} textAlign="center" color="gray.500">No line items found.</Td>
                              </Tr>
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    </CardBody>
                  </Card>
                )}
              </TabPanel>

              {/* Tab 3: Strategy Overview */}
              <TabPanel px={0}>
                {!draftContent?.strategy_overview ? (
                  <Card><CardBody><Text textAlign="center" color="gray.500" py={8}>No strategy generated yet.</Text></CardBody></Card>
                ) : (
                  <Card bg="blue.50" borderColor="blue.200" borderWidth="1px">
                    <CardBody>
                      <Heading size="sm" color="blue.800" mb={4}>Strategy Overview</Heading>
                      <Text color="blue.900" whiteSpace="pre-wrap">{draftContent.strategy_overview}</Text>
                    </CardBody>
                  </Card>
                )}
              </TabPanel>

              {/* Tab 4: Corrected Claim Memo */}
              <TabPanel px={0}>
                {!draftContent?.corrected_claim_memo ? (
                  <Card><CardBody><Text textAlign="center" color="gray.500" py={8}>No memo generated yet.</Text></CardBody></Card>
                ) : (
                  <Card>
                    <CardHeader bg="orange.50" borderTopRadius="md">
                      <Heading size="sm" color="orange.800">Corrected Claim Memo</Heading>
                    </CardHeader>
                    <Divider />
                    <CardBody>
                      <ReactMarkdown components={MarkdownComponents}>
                        {cleanMarkdownText(draftContent.corrected_claim_memo)}
                      </ReactMarkdown>
                    </CardBody>
                  </Card>
                )}
              </TabPanel>

              {/* Tab 5: Formal Appeal Letter */}
              <TabPanel px={0}>
                {!draftContent?.appeal_letter ? (
                  <Card><CardBody><Text textAlign="center" color="gray.500" py={8}>No appeal letter generated yet.</Text></CardBody></Card>
                ) : (
                  <Card>
                    <CardHeader bg="red.50" borderTopRadius="md">
                      <Heading size="sm" color="red.800">Formal Appeal Letter</Heading>
                    </CardHeader>
                    <Divider />
                    <CardBody>
                      <ReactMarkdown components={MarkdownComponents}>
                        {cleanMarkdownText(draftContent.appeal_letter)}
                      </ReactMarkdown>
                    </CardBody>
                  </Card>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>

        </VStack>
      )}
      </Box>
    </Box>
  );
};

export default ClaimAnalysisDashboard;
